import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AUTH_SIGNIN_URL } from './config/apiConfig';
import './css/CreatorLogin.css';

function CreatorLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('🔐 Attempting login to:', AUTH_SIGNIN_URL);
      console.log('📧 Email:', email);
      
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(AUTH_SIGNIN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      console.log('📥 Response status:', response.status);

      const data = await response.json();
      console.log('📦 Response data:', data);

      if (!response.ok) {
        if (response.status === 401) {
          setError('Invalid email or password. Please check your credentials.');
        } else if (response.status === 403) {
          setError('Email not confirmed. Please check your email to confirm your account.');
        } else {
          setError(data.error || 'Login failed. Please try again.');
        }
        setLoading(false);
        return;
      }

      if (data.success && data.session) {
        // Store session with tokens and user_id from Supabase
        const session = {
          email: data.user.email,
          userId: data.user.id, // Supabase user ID
          user_id: data.user.id, // Alternative key for consistency
          creator_id: data.user.creator_id, // Creator ID from backend profiles table
          CreatorId: data.user.creator_id, // Legacy key for compatibility
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
          expires_at: data.session.expires_at,
          loggedInAt: Date.now(),
        };
        
        console.log('✅ Login successful');
        console.log('User ID (Supabase):', data.user.id);
        console.log('Creator ID:', data.user.creator_id);
        console.log('Email:', data.user.email);
        console.log('Session stored:', session);
        
        localStorage.setItem('savory_creator', JSON.stringify(session));
        
        // Don't set loading to false here - let navigation handle it
        navigate('/creators/dashboard');
      } else {
        console.error('❌ Login failed - invalid response structure:', data);
        setError('Login failed. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error('❌ Login error:', err);
      if (err.name === 'AbortError') {
        setError('Request timeout. Please check if the backend server is running on localhost:5000');
      } else if (err.message.includes('fetch')) {
        setError('Cannot connect to server. Please ensure the backend is running on localhost:5000');
      } else {
        setError('Network error. Please check your connection and try again.');
      }
      setLoading(false);
    }
  };

  return (
    <div className="creator-login-screen">
      <div className="creator-login-container">
        <Link to="/creators" className="creator-login-back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to Creators
        </Link>

        <motion.div
          className="creator-login-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h1 className="creator-login-title">Creator login</h1>
          <p className="creator-login-subtitle">
            Sign in to manage your recipes and creator profile
          </p>

          <form onSubmit={handleSubmit} className="creator-login-form">
            {error && <p className="creator-login-error">{error}</p>}
            <label className="creator-login-label">
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="creator-login-input"
                placeholder="you@example.com"
                required
              />
            </label>
            <label className="creator-login-label">
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="creator-login-input"
                placeholder="••••••••"
                required
              />
            </label>
            <button
              type="submit"
              className="creator-login-button"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <p className="creator-login-footer">
            New to Savory? <Link to="/creators">Become a creator</Link> to get started.
          </p>
        </motion.div>
      </div>
    </div>
  );
}

export default CreatorLogin;
